// FULL-VIOLENCE Favorites System
class Favorites500 {
  constructor() {
    this.storageKey = 'fv_favorites';
    this.collectionStorageKey = 'fv_favorite_collections';
    this.favorites = this.loadFavorites();
    this.favoriteCollections = this.loadFavoriteCollections();
    this.init();
  }

  init() {
    // Initialize all favorite buttons
    this.updateAllButtons();

    // Listen for clicks on favorite buttons
    document.addEventListener('click', (e) => {
      const favButton = e.target.closest('.favorite-btn-500');
      if (favButton) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFavorite(favButton);
      }
    });

    // Dispatch event for pages to listen to
    window.dispatchEvent(new CustomEvent('favorites:loaded', {
      detail: { favorites: this.favorites }
    }));
  }

  loadFavorites() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites:', error);
      return [];
    }
  }

  loadFavoriteCollections() {
    try {
      const stored = localStorage.getItem(this.collectionStorageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorite collections:', error);
      return [];
    }
  }

  saveFavorites() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.favorites));

      // Dispatch update event
      window.dispatchEvent(new CustomEvent('favorites:updated', {
        detail: { favorites: this.favorites }
      }));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  }

  saveFavoriteCollections() {
    try {
      localStorage.setItem(this.collectionStorageKey, JSON.stringify(this.favoriteCollections));

      // Dispatch update event
      window.dispatchEvent(new CustomEvent('favorites:collections:updated', {
        detail: { collections: this.favoriteCollections }
      }));
    } catch (error) {
      console.error('Error saving favorite collections:', error);
    }
  }

  isFavorite(productId) {
    return this.favorites.includes(parseInt(productId));
  }

  isCollectionFavorite(collectionHandle) {
    return this.favoriteCollections.includes(collectionHandle);
  }

  addFavorite(productId, productData = {}) {
    const id = parseInt(productId);
    if (!this.isFavorite(id)) {
      this.favorites.push(id);
      this.saveFavorites();

      // Show feedback
      this.showNotification('Item has been added to your Favorites', 'success');

      return true;
    }
    return false;
  }

  removeFavorite(productId) {
    const id = parseInt(productId);
    const index = this.favorites.indexOf(id);

    if (index > -1) {
      this.favorites.splice(index, 1);
      this.saveFavorites();

      // Show feedback
      this.showNotification('Item has been removed from your Favorites', 'info');

      return true;
    }
    return false;
  }

  addCollectionFavorite(collectionHandle) {
    if (!this.isCollectionFavorite(collectionHandle)) {
      this.favoriteCollections.push(collectionHandle);
      this.saveFavoriteCollections();
      this.showNotification('Item has been added to your Favorites', 'success');
      return true;
    }
    return false;
  }

  removeCollectionFavorite(collectionHandle) {
    const index = this.favoriteCollections.indexOf(collectionHandle);
    if (index > -1) {
      this.favoriteCollections.splice(index, 1);
      this.saveFavoriteCollections();
      this.showNotification('Item has been removed from your Favorites', 'info');
      return true;
    }
    return false;
  }

  toggleFavorite(button) {
    // Check if it's a collection or product button
    const collectionHandle = button.dataset.collectionHandle;
    const productId = button.dataset.productId;

    if (collectionHandle) {
      // Handle collection favorite
      const isFav = this.isCollectionFavorite(collectionHandle);
      if (isFav) {
        this.removeCollectionFavorite(collectionHandle);
      } else {
        this.addCollectionFavorite(collectionHandle);
      }
      this.updateButton(button, !isFav);
    } else if (productId) {
      // Handle product favorite
      const isFav = this.isFavorite(productId);
      if (isFav) {
        this.removeFavorite(productId);
      } else {
        this.addFavorite(productId);
      }
      this.updateButton(button, !isFav);
    }

    // Update count in header if exists
    this.updateFavoriteCount();
  }

  updateButton(button, isFavorite) {
    if (isFavorite) {
      button.classList.add('is-favorite');
      button.setAttribute('aria-label', 'Remove from favorites');

      // Trigger animation
      button.classList.add('animate-favorite');
      setTimeout(() => button.classList.remove('animate-favorite'), 600);
    } else {
      button.classList.remove('is-favorite');
      button.setAttribute('aria-label', 'Add to favorites');
    }
  }

  updateAllButtons() {
    document.querySelectorAll('.favorite-btn-500').forEach(button => {
      const collectionHandle = button.dataset.collectionHandle;
      const productId = button.dataset.productId;

      if (collectionHandle) {
        this.updateButton(button, this.isCollectionFavorite(collectionHandle));
      } else if (productId) {
        this.updateButton(button, this.isFavorite(productId));
      }
    });
  }

  updateFavoriteCount() {
    const countElements = document.querySelectorAll('.favorite-count-500');
    const count = this.favorites.length;

    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline' : 'none';
    });
  }

  showNotification(message, type = 'success') {
    // Check if there's a notification system already
    if (window.theme && window.theme.notifier) {
      // Use theme's notification system
      return;
    }

    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = `favorite-toast-500 favorite-toast--${type}`;
    toast.textContent = message;

    document.body.appendChild(toast);

    // Trigger animation
    setTimeout(() => toast.classList.add('show'), 10);

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  getFavorites() {
    return this.favorites;
  }

  getFavoriteCount() {
    return this.favorites.length;
  }

  clearAllFavorites() {
    this.favorites = [];
    this.favoriteCollections = [];
    this.saveFavorites();
    this.saveFavoriteCollections();
    this.updateAllButtons();
    this.updateFavoriteCount();
    this.showNotification('All favorites cleared', 'info');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.Favorites500 = new Favorites500();
  });
} else {
  window.Favorites500 = new Favorites500();
}

// Handle Shopify theme editor
document.addEventListener('shopify:section:load', () => {
  if (window.Favorites500) {
    window.Favorites500.updateAllButtons();
    window.Favorites500.updateFavoriteCount();
  }
});
